import { HouseIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { tagLabel } from '@/lib/tags'
import { cn } from '@/lib/utils'

export type TagLinkProps = {
  className: string
  children: ReactNode
  'aria-current': 'page' | undefined
  'data-tag': string
}

/**
 * Only the items with a tag: All tags, then each tag (personal ones with a house). The caller
 * makes the links (its route keeps its other filters), so the filter is in the address and Back
 * works.
 */
export function TagFilter({
  tags,
  active,
  personalTags,
  link,
}: {
  tags: readonly string[]
  active: string | undefined
  personalTags?: readonly string[]
  link: (tag: string | undefined, props: TagLinkProps) => ReactNode
}) {
  const options: (string | undefined)[] = [undefined, ...tags]
  return (
    <nav aria-label="Tags" className="flex flex-wrap items-center gap-1" data-testid="tag-filter">
      {options.map((tag) => {
        const selected = tag === active
        const personal = tag !== undefined && (personalTags?.includes(tag) ?? false)
        return (
          <span key={tag ?? 'all'} className="contents">
            {link(tag, {
              'aria-current': selected ? 'page' : undefined,
              'data-tag': tag ?? 'all',
              className: cn(
                'inline-flex h-7 items-center gap-1 rounded-full border px-2.5 text-xs font-medium text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50',
                selected && 'border-foreground/20 bg-muted text-foreground',
              ),
              children: (
                <>
                  {personal ? <HouseIcon aria-label="Personal" className="size-3" /> : null}
                  {tag === undefined ? 'All tags' : tagLabel(tag)}
                </>
              ),
            })}
          </span>
        )
      })}
    </nav>
  )
}
